import * as React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Localized } from "@fluent/react";

type ProjectStatusPillProps = {
    status: string | null | undefined;
    size?: "small" | "medium";
    coloredDot?: boolean;
    maxWidth?: number | string;
    fullWidth?: boolean;
    dense?: boolean;
    truncate?: boolean;
    className?: string;
};

const STATUS_MAP = {
    D: { labelId: "project-status-d", fallback: "DRAFT", color: "primary" },
    I: { labelId: "project-status-i", fallback: "IN PROGRESS", color: "info" },
    H: { labelId: "project-status-h", fallback: "ON HOLD", color: "warning" },
    F: { labelId: "project-status-f", fallback: "FINISHED", color: "success" },
    C: { labelId: "project-status-c", fallback: "CANCELLED", color: "error" },
} as const satisfies Record<string, { labelId: string; fallback: string; color: PaletteKey }>;

type PaletteKey = "primary" | "secondary" | "error" | "info" | "success" | "warning";

const paletteMain = (palette: any, key: PaletteKey) => palette[key].main as string;

export const ProjectStatusPill: React.FC<ProjectStatusPillProps> = ({
    status,
    size = "small",
    coloredDot = true,
    maxWidth,
    fullWidth = false,
    dense = true,
    truncate = false,
    className,
}) => {
    const t = useTheme();

    const key = (status ?? "").toString().trim().toUpperCase() as string;
    const entry = STATUS_MAP[key] ?? { labelId: "status", fallback: "UNKNOWN", color: "info" as PaletteKey };

    const isSmall = size === "small";
    const height = isSmall ? 24 : 28;
    const fontSize = isSmall ? "0.75rem" : "0.8125rem";
    const labelPx = dense ? (isSmall ? 6 : 8) : (isSmall ? 8 : 10);
    const labelPy = dense ? (isSmall ? 1 : 2) : (isSmall ? 2 : 3);
    const gap = dense ? 0.5 : 0.75;
    const dotSize = dense ? 7 : 8;

    const main = paletteMain(t.palette, entry.color);
    const bgColor = alpha(main, t.palette.mode === "dark" ? 0.16 : 0.10);
    const brdColor = alpha(main, 0.5);
    const dotBg = coloredDot ? main : t.palette.text.secondary;

    return (
        <Box
            className={className}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                height,
                border: "1px solid",
                borderColor: brdColor,
                bgcolor: bgColor,
                borderRadius: 1,
                pl: labelPx,
                pr: labelPx,
                boxSizing: "border-box",
                maxWidth: maxWidth ?? undefined,
                width: fullWidth ? "100%" : "auto",
                overflow: truncate ? "hidden" : "visible",
                textOverflow: truncate ? "ellipsis" : "clip",
                flex: "0 0 auto",
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    width: dotSize,
                    height: dotSize,
                    borderRadius: "50%",
                    bgcolor: dotBg,
                    mr: gap,
                    flexShrink: 0,
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 800,
                    letterSpacing: ".02em",
                    color: t.palette.text.primary,
                    lineHeight: 1.6,
                    fontSize,
                    textTransform: "uppercase",
                    whiteSpace: truncate ? "nowrap" : "normal",
                    overflow: truncate ? "hidden" : "visible",
                    textOverflow: truncate ? "ellipsis" : "clip",
                    py: `${labelPy}px`,
                }}
            >
                <Localized id={entry.labelId}>
                    <span>{entry.fallback}</span>
                </Localized>
            </Typography>
        </Box>
    );
};
